import * as React from "react";
import { HashRouter } from "react-router-dom";
import { RecoilRoot } from "recoil";
import { EPlatform } from "../api";

import { FullPageLoader } from "../components";
import {
  ActionHistoryProvider,
  AuthProvider,
  BreadcrumbProvider,
  DialogProvider,
  EventsProvider,
  GlobalDataProvider,
  NavigationProvider,
  PlatformProvider,
  SearchCriteriaProvider,
  useDialog,
} from "../contexts";
import { useTl } from "../hooks";
import { ETLCodes } from "../locales";

const DrupalAppWithRouterProvider: React.FunctionComponent<React.PropsWithChildren> = (props) => {
  const { showDialog } = useDialog();
  const { t } = useTl();
  const getUserConfirmation = React.useCallback(
    (message, cb) => {
      showDialog({
        message,
        title: t(ETLCodes.RouteConfirmDialogTitle),
        onDialogClosed: (res) => {
          if (res === "yes" || res === "ok") {
            cb(true);
          } else {
            cb(false);
          }
        },
      });
    },
    [showDialog, t]
  );
  const memoizedChildren = React.useMemo(() => props.children, [props.children]);
  return (
    <HashRouter getUserConfirmation={getUserConfirmation}>
      <EventsProvider>
        <AuthProvider>
          <GlobalDataProvider>
            <NavigationProvider>
              <ActionHistoryProvider>
                <BreadcrumbProvider>
                  <SearchCriteriaProvider>{memoizedChildren}</SearchCriteriaProvider>
                </BreadcrumbProvider>
              </ActionHistoryProvider>
            </NavigationProvider>
          </GlobalDataProvider>
        </AuthProvider>
      </EventsProvider>
    </HashRouter>
  );
};

export const DrupalAppProvider: React.FunctionComponent<React.PropsWithChildren> = (props) => {
  const memoizedChildren = React.useMemo(() => props.children, [props.children]);
  return (
    <React.Suspense fallback={<FullPageLoader />}>
      <RecoilRoot>
        <PlatformProvider platform={EPlatform.PORTAL}>
          <DialogProvider>
            <DrupalAppWithRouterProvider>{memoizedChildren}</DrupalAppWithRouterProvider>
          </DialogProvider>
        </PlatformProvider>
      </RecoilRoot>
    </React.Suspense>
  );
};
